import type { SupportedLanguage } from '@/i18n';

/**
 * The "Contact us" line in the Smart Form header.
 *
 * **Keyed by PLATFORM, not by form.** BMO's table splits the numbers by which
 * back office the application lands in, and that is already a property of the
 * workflow (`template.platform`), so nothing here needs a per-form list that
 * would go stale the moment a sixth sub-agreement is added:
 *
 *   IFDS — the registered plans (RDSP, RESP, TFSA, RIF, Reg / Non-Reg)
 *   APEX — the subscription agreements (Partners Group, both Carlyle,
 *          Alpha Managers, ALTitude EQT)
 *
 * Two things in the source table are worth knowing before reading the values:
 *
 * 1. **IFDS uses a DIFFERENT number per language** — the French line is not a
 *    translation of the English one, it is a different toll-free number. APEX
 *    uses the same number for both.
 * 2. **BMO's table gives the APEX French row in ENGLISH** ("Contact us: …"),
 *    which reads like an oversight rather than a decision. Translating it
 *    ourselves would be inventing copy for a client-facing line, so the English
 *    label is reproduced verbatim and flagged — see `apexFrenchLabelIsEnglish`.
 *
 * Hours are carried separately because the mobile layout drops them (AC 1.2)
 * while keeping the number.
 */
export type ContactDetails = {
  /** "Contact us:" / "Contactez-nous :" — the lead-in, without the number. */
  label: string;
  /** Rendered as a `tel:` link, so it stays as BMO writes it. */
  phone: string;
  /** Business hours. Hidden on mobile; absent when BMO gives none. */
  hours?: string[];
};

/** Which back office an application lands in — mirrors the domain enum. */
export type ContactPlatform = 'IFDS' | 'APEX';

const IFDS: Record<SupportedLanguage, ContactDetails> = {
  en: {
    label: 'Contact us: Toll-Free',
    phone: '1-800-668-7327',
    hours: ['Mon to Thu – 8:30am to 7:00pm EST', 'Fri – 8:30am to 6:00pm EST'],
  },
  fr: {
    // A different number, not a translation of the English one.
    label: 'Contactez-nous : Sans frais',
    phone: '1 (800) 304-7151',
    hours: ['Lun au jeu – 8 h 30 à 19 h HNE', 'Ven – 8 h 30 à 18 h HNE'],
  },
};

const APEX: Record<SupportedLanguage, ContactDetails> = {
  en: {
    label: 'Contact us: Toll-Free',
    phone: '1-800-361-1392',
  },
  // Verbatim from BMO's table, which gives this row in English. See the header
  // note — translating it here would be inventing client-facing copy.
  fr: {
    label: 'Contact us: Toll-Free',
    phone: '1-800-361-1392',
  },
};

/**
 * True when the French APEX label is still the English string — i.e. BMO has
 * not supplied a French translation for it. Exported so a test can assert the
 * gap is deliberate rather than an accident, and so it is easy to find and
 * remove the day the translation arrives.
 */
export const apexFrenchLabelIsEnglish = APEX.fr.label === APEX.en.label;

/**
 * The contact line for a workflow. Defaults to IFDS: the registered plans are
 * the larger family, and an unknown platform showing the registered-plan desk
 * is a better failure than showing nothing at all.
 */
export function contactDetailsFor(
  platform: ContactPlatform | string | null | undefined,
  language: SupportedLanguage,
): ContactDetails {
  const table = String(platform).toUpperCase() === 'APEX' ? APEX : IFDS;
  return table[language];
}
